package com.evoapps.evoshift.widgets

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.ui.AppAppearance
import com.evoapps.evoshift.ui.Palettes
import kotlin.math.abs

/*
 * SH-225: a period against the one it is compared with: this period's bars (filled) beside the
 * comparison's (outlined), and under them the difference, up or down from a zero line, marked
 * "+" and "−" (never colour alone). A slot without one of the sides has no difference bar.
 *
 * SH-252: with nothing to compare (no slot has both sides) the difference band and its legend are
 * left out, and without any comparison figure this period's bars take the whole slot. Bars keep a
 * sensible width on a wide screen (never wider than maxBarWidth).
 */

/** The test tag of the difference band under a chart tagged [chartTag]. */
fun comparisonDifferenceTag(chartTag: String) = "${chartTag}_difference"

/** One slot (a day, a week): this period's figure, the comparison's, and the change between them; null: no data. */
@Immutable
class ComparisonSlot(
    val label: String,
    val current: Double?,
    val base: Double?,
    /** current − base; null when a side is missing. */
    val difference: Double?,
    /** The change in % of the base; null when a side is missing or the base is 0. */
    val changePercent: Double?,
    /** A public holiday on either side (marked under the slot). */
    val marked: Boolean = false
)

/**
 * The comparison chart's colours, for the surface it is drawn on (the cream cards): this period
 * in the base colour, the comparison in the deep accent, a rise in green and a fall in red, each
 * at least 3:1 against the card in every theme (ComparisonChartContrastTest).
 */
@Immutable
data class ComparisonChartColours(val current: Color, val base: Color, val rise: Color, val fall: Color, val axis: Color) {
    companion object {
        fun of(appearance: Appearance): ComparisonChartColours {
            val p = Palettes.of(appearance)
            val onScreen = onScreenColours(appearance)
            return ComparisonChartColours(
                current = p.base.base,
                base = p.accent.deep,
                rise = onScreen.success,
                fall = onScreen.error,
                axis = p.base.onLightVariant
            )
        }

        /** The colours of the appearance on show. */
        val current: ComparisonChartColours get() = of(AppAppearance.current)
    }
}

/**
 * [slots] side by side; hovering, tapping or dragging picks one, its figures in [readout] above.
 * With [changeLabels] each slot's change in % is written over it ([changeFormat]).
 */
@Composable
fun ComparisonChart(
    slots: List<ComparisonSlot>,
    currentLabel: String,
    baseLabel: String,
    riseLabel: String,
    fallLabel: String,
    markedLabel: String,
    description: String,
    axisFormat: (Double) -> String,
    changeFormat: (Double) -> String,
    modifier: Modifier = Modifier,
    colours: ComparisonChartColours = ComparisonChartColours.current,
    changeLabels: Boolean = false,
    initialSelection: Int? = null,
    labelEvery: Int = 1,
    height: Dp = 170.dp,
    differenceHeight: Dp = 64.dp,
    /** SH-252: the widest a single bar gets (a few days on a wide screen stay bars, not blocks). */
    maxBarWidth: Dp = 22.dp,
    testTag: String? = null,
    /** SH-228: a small mark under each slot (the day's weather, a special day); null: none. */
    slotMarker: (@Composable (index: Int) -> Unit)? = null,
    readout: @Composable (index: Int) -> Unit
) {
    val count = slots.size
    if (count == 0) return
    var selected by remember(count) { mutableStateOf((initialSelection ?: (count - 1)).coerceIn(0, count - 1)) }
    val measurer = rememberTextMeasurer()
    val axisStyle = MaterialTheme.typography.labelSmall.copy(fontSize = 10.sp, color = colours.axis)
    val selectedAxisStyle = axisStyle.copy(color = colours.current, fontWeight = FontWeight.Bold)
    val changeStyle = MaterialTheme.typography.labelSmall.copy(fontSize = 10.sp, fontWeight = FontWeight.Bold)
    // SH-228: a daily result can be a loss: the bars then hang below a zero line.
    val barMax = remember(slots) { niceCeil((slots.flatMap { listOf(it.current, it.base) }.filterNotNull().maxOrNull() ?: 0.0).coerceAtLeast(0.0)) }
    val barMin = remember(slots) {
        val lowest = slots.flatMap { listOf(it.current, it.base) }.filterNotNull().minOrNull() ?: 0.0
        if (lowest < 0) -niceCeil(-lowest) else 0.0
    }
    val diffMax = remember(slots) { slots.mapNotNull { it.difference?.let(::abs) }.maxOrNull() ?: 0.0 }
    // SH-252: nothing compared, no difference band; no comparison figure at all, one bar per slot.
    val hasDifference = remember(slots) { slots.any { it.difference != null } }
    val hasBase = remember(slots) { slots.any { it.base != null && it.base != 0.0 } }
    val bandHeight = if (hasDifference) differenceHeight else 0.dp

    Column(modifier) {
        Box(Modifier.fillMaxWidth().heightIn(min = 44.dp)) { readout(selected) }
        Spacer(Modifier.height(8.dp))
        Box(Modifier.fillMaxWidth()) {
        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(height + bandHeight)
                .let { if (testTag != null) it.testTag(testTag) else it }
                .semantics { contentDescription = description }
                .pointerInput(count) {
                    awaitPointerEventScope {
                        while (true) {
                            val event = awaitPointerEvent()
                            if (event.type == PointerEventType.Exit) continue
                            val x = event.changes.firstOrNull()?.position?.x ?: continue
                            val left = 44.dp.toPx()
                            val slot = (size.width - left - 8.dp.toPx()) / count
                            if (slot > 0) selected = ((x - left) / slot).toInt().coerceIn(0, count - 1)
                        }
                    }
                }
        ) {
            val left = 44.dp.toPx()
            val right = 8.dp.toPx()
            val labelsH = 16.dp.toPx()
            val topPad = if (changeLabels) 14.dp.toPx() else 4.dp.toPx()
            val plotW = size.width - left - right
            val plotH = height.toPx() - labelsH - topPad
            val plotTop = topPad
            val plotBottom = plotTop + plotH
            val diffTop = height.toPx()
            val diffH = (bandHeight.toPx() - 6.dp.toPx()).coerceAtLeast(0f)
            val zero = diffTop + diffH / 2
            val slotW = plotW / count
            val grid = colours.axis.copy(alpha = 0.18f)

            // The picked slot, across both bands.
            drawRoundRect(
                colours.current.copy(alpha = 0.06f),
                topLeft = Offset(left + slotW * selected, 0f),
                size = Size(slotW, if (hasDifference) diffTop + diffH else plotBottom),
                cornerRadius = CornerRadius(4.dp.toPx())
            )

            val span = (barMax - barMin).takeIf { it > 0 } ?: 1.0
            fun yOf(v: Double): Float = plotBottom - ((v - barMin) / span * plotH).toFloat()

            // Grid and the axis (0, half, max; with losses: min, 0, max).
            val ticks = if (barMin < 0) listOf(barMin, 0.0, barMax) else listOf(0.0, barMax / 2, barMax)
            ticks.forEach { v ->
                val y = yOf(v)
                drawLine(if (v == 0.0 && barMin < 0) colours.axis.copy(alpha = 0.6f) else grid, Offset(left, y), Offset(left + plotW, y), strokeWidth = 1.dp.toPx())
                val text = measurer.measure(axisFormat(v), axisStyle)
                drawText(text, topLeft = Offset(left - text.size.width - 6.dp.toPx(), y - text.size.height / 2))
            }

            // This period filled, the comparison outlined, side by side.
            if (barMax > 0 || barMin < 0) {
                val maxBar = maxBarWidth.toPx()
                val group = if (hasBase) minOf(slotW * 0.8f, maxBar * 2 / 0.88f) else minOf(slotW * 0.7f, maxBar / 0.88f)
                val barW = if (hasBase) group / 2 else group
                val radius = CornerRadius(minOf(barW / 2, 3.dp.toPx()))
                slots.forEachIndexed { i, slot ->
                    val x0 = left + slotW * i + (slotW - group) / 2
                    slot.current?.takeIf { it != 0.0 }?.let { v ->
                        val top = yOf(maxOf(v, 0.0))
                        val h = yOf(minOf(v, 0.0)) - top
                        drawRoundRect(colours.current, Offset(x0 + barW * 0.06f, top), Size(barW * 0.88f, h), radius)
                    }
                    slot.base?.takeIf { it != 0.0 }?.let { v ->
                        val top = yOf(maxOf(v, 0.0))
                        val h = yOf(minOf(v, 0.0)) - top
                        val stroke = 1.5.dp.toPx()
                        val topLeft = Offset(x0 + barW + barW * 0.06f + stroke / 2, top + stroke / 2)
                        val barSize = Size((barW * 0.88f - stroke).coerceAtLeast(1f), (h - stroke).coerceAtLeast(1f))
                        drawRoundRect(colours.base.copy(alpha = 0.22f), topLeft, barSize, radius)
                        drawRoundRect(colours.base, topLeft, barSize, radius, style = Stroke(width = stroke))
                    }
                    if (changeLabels) {
                        val text = measurer.measure(slot.changePercent?.let(changeFormat) ?: "–", changeStyle.copy(
                            color = when {
                                slot.changePercent == null -> colours.axis
                                slot.changePercent >= 0 -> colours.rise
                                else -> colours.fall
                            }
                        ))
                        val tx = left + slotW * i + slotW / 2 - text.size.width / 2
                        drawText(text, topLeft = Offset(tx.coerceIn(0f, size.width - text.size.width), 0f))
                    }
                }
            }

            // X labels: every [labelEvery]th slot and the picked one; a holiday's ring under its label.
            slots.forEachIndexed { i, slot ->
                val cx = left + slotW * i + slotW / 2
                if (slot.marked) {
                    drawCircle(colours.current, radius = 2.5.dp.toPx(), center = Offset(cx, plotBottom + labelsH - 1.dp.toPx()), style = Stroke(1.2.dp.toPx()))
                }
                val regular = i % labelEvery == 0 && (labelEvery == 1 || abs(i - selected) > labelEvery / 2)
                if (!regular && i != selected) return@forEachIndexed
                val text = measurer.measure(slot.label, if (i == selected) selectedAxisStyle else axisStyle)
                val x = cx - text.size.width / 2
                drawText(text, topLeft = Offset(x.coerceIn(0f, size.width - text.size.width), plotBottom + 1.dp.toPx()))
            }

            // The difference band: a zero line, "+" above it and "−" below, bars up or down.
            if (!hasDifference) return@Canvas
            drawLine(colours.axis.copy(alpha = 0.6f), Offset(left, zero), Offset(left + plotW, zero), strokeWidth = 1.dp.toPx())
            drawLine(
                grid, Offset(left, diffTop), Offset(left + plotW, diffTop), strokeWidth = 1.dp.toPx(),
                pathEffect = PathEffect.dashPathEffect(floatArrayOf(4f, 4f))
            )
            listOf("+" to diffTop + diffH * 0.2f, "0" to zero, "−" to diffTop + diffH * 0.8f).forEach { (sign, y) ->
                val text = measurer.measure(sign, axisStyle.copy(fontWeight = FontWeight.Bold))
                drawText(text, topLeft = Offset(left - text.size.width - 6.dp.toPx(), y - text.size.height / 2))
            }
            if (diffMax > 0) {
                val barW = minOf(slotW * 0.5f, maxBarWidth.toPx())
                slots.forEachIndexed { i, slot ->
                    val diff = slot.difference ?: return@forEachIndexed
                    if (diff == 0.0) return@forEachIndexed
                    val h = (abs(diff) / diffMax * (diffH / 2 - 2.dp.toPx())).toFloat().coerceAtLeast(1.dp.toPx())
                    val x = left + slotW * i + (slotW - barW) / 2
                    val colour = if (diff > 0) colours.rise else colours.fall
                    val top = if (diff > 0) zero - h else zero
                    drawRoundRect(colour, Offset(x, top), Size(barW, h), CornerRadius(minOf(barW / 2, 2.dp.toPx())))
                }
            }
        }
            if (hasDifference && testTag != null) {
                // The band's place, so tests can tell it is drawn.
                Box(Modifier.align(Alignment.BottomStart).fillMaxWidth().height(bandHeight).testTag(comparisonDifferenceTag(testTag)))
            }
        }
        if (slotMarker != null) {
            // Under each slot, lined up with the bars (the plot's 44dp axis on the left, 8dp on the right).
            Row(Modifier.fillMaxWidth().padding(start = 44.dp, end = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                repeat(count) { i ->
                    Box(Modifier.weight(1f), contentAlignment = Alignment.Center) { slotMarker(i) }
                }
            }
        }
        Spacer(Modifier.height(10.dp))
        ComparisonLegend(
            colours, currentLabel, baseLabel.takeIf { hasBase }, riseLabel.takeIf { hasDifference }, fallLabel.takeIf { hasDifference },
            markedLabel.takeIf { slots.any { it.marked } }
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ComparisonLegend(colours: ComparisonChartColours, current: String, base: String?, rise: String?, fall: String?, marked: String?) {
    FlowRow(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        LegendSwatch(current, colours.axis) { Box(Modifier.size(10.dp).clip(RoundedCornerShape(2.dp)).background(colours.current)) }
        if (base != null) LegendSwatch(base, colours.axis) {
            Box(Modifier.size(10.dp).clip(RoundedCornerShape(2.dp)).background(colours.base.copy(alpha = 0.22f)).border(1.5.dp, colours.base, RoundedCornerShape(2.dp)))
        }
        if (rise != null) LegendSwatch("+ $rise", colours.axis) { Box(Modifier.size(10.dp).clip(RoundedCornerShape(2.dp)).background(colours.rise)) }
        if (fall != null) LegendSwatch("− $fall", colours.axis) { Box(Modifier.size(10.dp).clip(RoundedCornerShape(2.dp)).background(colours.fall)) }
        if (marked != null) {
            LegendSwatch(marked, colours.axis) { Box(Modifier.size(7.dp).border(1.2.dp, colours.current, CircleShape)) }
        }
    }
}

@Composable
private fun LegendSwatch(label: String, textColour: Color, swatch: @Composable () -> Unit) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        swatch()
        Spacer(Modifier.width(6.dp))
        Text(label, style = MaterialTheme.typography.labelSmall, color = textColour)
    }
}
